var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
/**
 * A customizable button component.
 *
 * @slot - The button content (text)
 * @slot icon - Optional icon to display alongside the button text
 * @fires click - Fired when the button is clicked
 * @fires option-select - Fired when a dropdown option is selected
 */
export class KRButton extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The button variant (shape)
         */
        this.variant = 'flat';
        /**
         * The button color
         */
        this.color = 'primary';
        /**
         * The button size
         */
        this.size = 'medium';
        /**
         * Whether the button is disabled
         */
        this.disabled = false;
        /**
         * Dropdown options - when provided, button becomes a dropdown
         */
        this.options = [];
        /**
         * Position of the icon relative to the text content.
         * @attr icon-position
         */
        this.iconPosition = 'left';
        /**
         * When true and options are provided, renders as a split button with
         * separate primary action and dropdown trigger zones.
         */
        this.split = false;
        this._state = 'idle';
        this._stateText = '';
        this._dropdownOpened = false;
        this._handleHostClick = (e) => {
            if (this.split && this.options.length) {
                return;
            }
            if (this.options.length) {
                e.stopPropagation();
                this._toggleDropdown();
            }
        };
        this._handleKeydown = (e) => {
            if (e.key === 'Escape' && this._dropdownOpened) {
                this._dropdownOpened = false;
                return;
            }
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (this.split && this.options.length) {
                    this.dispatchEvent(new MouseEvent('click', {
                        bubbles: true,
                        composed: true
                    }));
                    return;
                }
                if (this.options.length) {
                    this._toggleDropdown();
                }
                else {
                    this.click();
                }
            }
            if (this.split && this.options.length && e.key === 'ArrowDown') {
                e.preventDefault();
                if (!this._dropdownOpened) {
                    this._toggleDropdown();
                }
            }
        };
        this._handleClickOutside = (e) => {
            if (this._dropdownOpened && !this.contains(e.target)) {
                this._dropdownOpened = false;
            }
        };
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.href) {
            this.setAttribute('role', 'link');
        }
        else {
            this.setAttribute('role', 'button');
        }
        this.setAttribute('tabindex', '0');
        this.addEventListener('keydown', this._handleKeydown);
        this.addEventListener('click', this._handleHostClick);
        document.addEventListener('click', this._handleClickOutside);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('keydown', this._handleKeydown);
        this.removeEventListener('click', this._handleHostClick);
        document.removeEventListener('click', this._handleClickOutside);
    }
    updated(changedProperties) {
        // Reflect state classes to host
        this.classList.toggle('kr-button--loading', this._state === 'loading');
        this.classList.toggle('kr-button--success', this._state === 'success');
        this.classList.toggle('kr-button--error', this._state === 'error');
        // Toggle every variant/color class explicitly so a runtime change (e.g. an
        // Install button flipping to Uninstall and back) drops the old class too.
        this.classList.toggle('kr-button--flat', this.variant === 'flat');
        this.classList.toggle('kr-button--outline', this.variant === 'outline');
        this.classList.toggle('kr-button--primary', this.color === 'primary');
        this.classList.toggle('kr-button--secondary', this.color === 'secondary');
        this.classList.toggle('kr-button--danger', this.color === 'danger');
        this.classList.toggle('kr-button--small', this.size === 'small');
        this.classList.toggle('kr-button--large', this.size === 'large');
        this.classList.toggle('kr-button--split', this.split && !!this.options.length);
    }
    _handleSplitPrimaryClick(e) {
        e.stopPropagation();
        if (this._dropdownOpened) {
            this._dropdownOpened = false;
        }
        this.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            composed: true,
        }));
    }
    _handleSplitTriggerClick(e) {
        e.stopPropagation();
        this._toggleDropdown();
    }
    _toggleDropdown() {
        this._dropdownOpened = !this._dropdownOpened;
        if (this._dropdownOpened) {
            // Position the fixed dropdown relative to the host element
            requestAnimationFrame(() => {
                const dropdown = this.shadowRoot?.querySelector('.dropdown');
                if (dropdown) {
                    const hostRect = this.getBoundingClientRect();
                    dropdown.style.top = hostRect.bottom + 4 + 'px';
                    dropdown.style.bottom = '';
                    dropdown.style.left = hostRect.left + 'px';
                    dropdown.style.right = '';
                    dropdown.style.minWidth = hostRect.width + 'px';
                    dropdown.style.transformOrigin = 'top left';
                    dropdown.style.maxHeight = '';
                    dropdown.style.overflowY = '';
                    const dropdownRect = dropdown.getBoundingClientRect();
                    // Open above only if the dropdown doesn't fit below and there's more room above than below
                    // 8 = 4px gap between button and dropdown + 4px margin from viewport edge
                    if (dropdownRect.height > window.innerHeight - hostRect.bottom - 8 && hostRect.top > window.innerHeight - hostRect.bottom) {
                        dropdown.style.top = '';
                        dropdown.style.bottom = window.innerHeight - hostRect.top + 4 + 'px';
                        dropdown.style.transformOrigin = 'bottom left';
                        dropdown.classList.add('dropdown--above');
                        if (dropdownRect.height > hostRect.top - 8) {
                            dropdown.style.maxHeight = hostRect.top - 8 + 'px';
                            dropdown.style.overflowY = 'auto';
                        }
                    }
                    else {
                        dropdown.classList.remove('dropdown--above');
                        if (dropdownRect.height > window.innerHeight - hostRect.bottom - 8) {
                            dropdown.style.maxHeight = window.innerHeight - hostRect.bottom - 8 + 'px';
                            dropdown.style.overflowY = 'auto';
                        }
                    }
                    // Align right if dropdown overflows viewport right
                    if (dropdownRect.right > window.innerWidth) {
                        dropdown.style.left = '';
                        dropdown.style.right = window.innerWidth - hostRect.right + 'px';
                        if (dropdownRect.bottom > window.innerHeight) {
                            dropdown.style.transformOrigin = 'bottom right';
                        }
                        else {
                            dropdown.style.transformOrigin = 'top right';
                        }
                    }
                }
            });
        }
    }
    _handleOptionClick(option, e) {
        e.stopPropagation();
        this._dropdownOpened = false;
        this.dispatchEvent(new CustomEvent('option-select', {
            detail: {
                id: option.id,
                label: option.label
            },
            bubbles: true,
            composed: true
        }));
    }
    /**
     * Shows a loading spinner and disables the button.
     */
    showLoading() {
        this._clearStateTimeout();
        this._state = 'loading';
        this._stateText = '';
    }
    /**
     * Shows a success state with optional custom text.
     * @param text - Text to display (default: "Saved")
     * @param duration - Duration in ms before auto-reset (default: 2000)
     */
    showSuccess(text = 'Success', duration = KRButton.STATE_DURATION) {
        this._clearStateTimeout();
        this._state = 'success';
        this._stateText = text;
        if (duration > 0) {
            this._stateTimeout = window.setTimeout(() => this.reset(), duration);
        }
    }
    /**
     * Shows an error state with optional custom text.
     * @param text - Text to display (default: "Error")
     * @param duration - Duration in ms before auto-reset (default: 2000)
     */
    showError(text = 'Error', duration = KRButton.STATE_DURATION) {
        this._clearStateTimeout();
        this._state = 'error';
        this._stateText = text;
        if (duration > 0) {
            this._stateTimeout = window.setTimeout(() => this.reset(), duration);
        }
    }
    /**
     * Returns whether the button is currently in the loading state.
     */
    isLoading() {
        return this._state === 'loading';
    }
    /**
     * Resets the button to its idle state.
     */
    reset() {
        this._clearStateTimeout();
        this._state = 'idle';
        this._stateText = '';
    }
    _clearStateTimeout() {
        if (this._stateTimeout) {
            clearTimeout(this._stateTimeout);
            this._stateTimeout = undefined;
        }
    }
    /**
     * One dropdown row. An option carrying `href` renders as a real anchor so it navigates
     * natively and honors modifier-clicks, matching how the whole button behaves when the
     * top-level `href` is set. Shared by the split and plain dropdown renders.
     */
    _renderDropdownItem(option) {
        if (option.href) {
            return html `
        <a
          class="dropdown-item"
          href=${option.href}
          target=${option.hrefTarget || nothing}
          @click=${this._handleOptionLinkClick}
        >${option.label}</a>
      `;
        }
        return html `
      <button
        class="dropdown-item"
        @click=${(e) => this._handleOptionClick(option, e)}
      >${option.label}</button>
    `;
    }
    // An href option navigates on its own, so this only dismisses the menu. The click is left
    // to propagate — consumers with client-side routers intercept anchor clicks themselves.
    _handleOptionLinkClick() {
        this._dropdownOpened = false;
    }
    render() {
        if (this.split && this.options.length) {
            return html `
        <div class="split-primary" @click=${this._handleSplitPrimaryClick}>
          <span class="content">
            <slot name="icon"></slot>
            <slot></slot>
          </span>
          ${this._state !== 'idle'
                ? html `<span class="state-overlay">
                ${this._state === 'loading'
                    ? html `<span class="spinner"></span>`
                    : this._stateText}
              </span>`
                : nothing}
        </div>
        <div class="split-divider"></div>
        <div class="split-trigger" @click=${this._handleSplitTriggerClick}>
          <svg
            class="caret"
            xmlns="http://www.w3.org/2000/svg"
            height="20"
            width="20"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
        </div>
        <div class=${classMap({
                'dropdown': true,
                'dropdown--opened': this._dropdownOpened
            })}>
          ${this.options.map(option => this._renderDropdownItem(option))}
        </div>
      `;
        }
        const content = html `
      <span class="content">
        <slot name="icon"></slot>
        <slot></slot>
      </span>
      ${this.options.length
            ? html `
          <svg
            class="caret"
            xmlns="http://www.w3.org/2000/svg"
            height="20"
            width="20"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
          </svg>
        `
            : nothing}
      ${this._state !== 'idle'
            ? html `
          <span class="state-overlay">
            ${this._state === 'loading'
                ? html `<span class="spinner"></span>`
                : this._stateText}
          </span>
        `
            : nothing}
      ${this.options.length
            ? html `
          <div class=${classMap({
                'dropdown': true,
                'dropdown--opened': this._dropdownOpened
            })}>
            ${this.options.map(option => this._renderDropdownItem(option))}
          </div>
        `
            : nothing}
    `;
        if (this.href) {
            return html `
        <a
          class="link"
          href=${this.href}
          target=${this.target || nothing}
        >${content}</a>
      `;
        }
        return content;
    }
}
KRButton.styles = css `
    :host {
      display: inline-flex;
      position: relative;
      align-items: center;
      justify-content: center;
      font-family: inherit;
      font-size: 14px;
      font-weight: 400;
      letter-spacing: 0.01em;
      padding: 0 22px;
      height: 36px;
      border: none;
      border-radius: 20px;
      cursor: pointer;
      transition: background 0.15s ease;
      user-select: none;
      box-sizing: border-box;
    }

    :host([disabled]),
    :host(.kr-button--loading),
    :host(.kr-button--success),
    :host(.kr-button--error) {
      cursor: not-allowed;
      pointer-events: none;
    }

    :host([disabled]:not(.kr-button--loading):not(.kr-button--success):not(.kr-button--error)) {
      opacity: 0.5;
    }

    /* Flat + Primary (default) */
    :host,
    :host(.kr-button--flat.kr-button--primary) {
      background: var(--kr-button-bg, #163052);
      color: var(--kr-button-color, white);
    }

    :host(:hover:not([disabled])),
    :host(.kr-button--flat.kr-button--primary:hover:not([disabled])) {
      background: var(--kr-button-hover-bg, #0e1f35);
    }

    /* Flat + Secondary */
    :host(.kr-button--flat.kr-button--secondary) {
      background: #f3f4f6;
      color: #374151;
    }

    :host(.kr-button--flat.kr-button--secondary:hover:not([disabled])) {
      background: #e5e7eb;
    }

    /* Outline + Primary */
    :host(.kr-button--outline.kr-button--primary) {
      background: transparent;
      border: 1px solid var(--kr-button-outline-border-color, #163052);
      color: var(--kr-button-outline-color, #163052);
    }

    :host(.kr-button--outline.kr-button--primary:hover:not([disabled])) {
      background: var(--kr-button-outline-hover-bg, rgba(22, 48, 82, 0.05));
    }

    /* Outline + Secondary */
    :host(.kr-button--outline.kr-button--secondary) {
      background: transparent;
      border: 1px solid #d1d5db;
      color: #374151;
    }

    :host(.kr-button--outline.kr-button--secondary:hover:not([disabled])) {
      background: #f9fafb;
      border-color: #9ca3af;
    }

    /* Flat + Danger */
    :host(.kr-button--flat.kr-button--danger) {
      background: #dc2626;
      color: white;
    }

    :host(.kr-button--flat.kr-button--danger:hover:not([disabled])) {
      background: #b91c1c;
    }

    /* Outline + Danger */
    :host(.kr-button--outline.kr-button--danger) {
      background: transparent;
      border: 1px solid rgb(219, 38, 39);
      color: rgb(219, 38, 39);
    }

    :host(.kr-button--outline.kr-button--danger:hover:not([disabled])) {
      background: rgba(219, 38, 39, 0.05);
    }

    /* Sizes */
    :host(.kr-button--small) {
      font-size: 13px;
      height: 30px;
      padding: 0 16px;
    }

    :host(.kr-button--large) {
      font-size: 16px;
      height: 44px;
      padding: 0 24px;
      border-radius: 30px;
    }

    /* State colors */
    :host(.kr-button--success) {
      background: #198754 !important;
      color: white !important;
    }

    :host(.kr-button--error) {
      background: rgb(220, 53, 69) !important;
      color: white !important;
    }

    /* Content */
    :host(.kr-button--loading) .content,
    :host(.kr-button--success) .content,
    :host(.kr-button--error) .content {
      visibility: hidden;
    }

    /* State overlay */
    .state-overlay {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Spinner */
    @keyframes kr-spin {
      to {
        transform: rotate(360deg);
      }
    }

    .spinner {
      width: 12px;
      height: 12px;
      border: 2.5px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      animation: kr-spin 0.6s linear infinite;
    }

    :host(.kr-button--small) .spinner {
      width: 10px;
      height: 10px;
      border-width: 2px;
    }

    :host(.kr-button--large) .spinner {
      width: 16px;
      height: 16px;
      border-width: 3px;
    }

    /* Dropdown */
    .dropdown {
      position: fixed;
      min-width: 100%;
      box-sizing: border-box;
      background: white;
      border: 1px solid #9ba7b6;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      padding: 8px 0;
      z-index: 10000;
      display: none;
      transform-origin: top left;
    }

    .dropdown--opened {
      display: block;
      animation: dropdown-fade-in 150ms ease-out;
    }

    .dropdown--above {
      animation-name: dropdown-fade-in-above;
    }

    @keyframes dropdown-fade-in {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes dropdown-fade-in-above {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* An item is a <button>, or an <a> when the option carries an href — box-sizing and
       text-decoration keep the anchor visually identical to the button. */
    .dropdown-item {
      display: block;
      box-sizing: border-box;
      width: 100%;
      padding: 8px 16px;
      text-align: left;
      background: none;
      border: none;
      font-size: 14px;
      color: #374151;
      text-decoration: none;
      cursor: pointer;
      font-family: inherit;
      white-space: nowrap;
    }

    .dropdown-item:hover {
      background: #f3f4f6;
    }

    /* Caret for dropdown button */
    .caret {
      margin-left: 4px;
      margin-right: -6px;
      display: flex;
    }

    /* Link wrapper */
    .link {
      color: inherit;
      text-decoration: none;
    }

    /* Content wrapper for icon + text layout */
    .content {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    :host([icon-position="right"]) .content {
      flex-direction: row-reverse;
    }

    /* Icon styling */
    ::slotted([slot="icon"]) {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    :host(.kr-button--small) ::slotted([slot="icon"]) {
      width: 16px;
      height: 16px;
    }

    :host(.kr-button--large) ::slotted([slot="icon"]) {
      width: 22px;
      height: 22px;
    }

    /* ========================
       Split Button
       ======================== */

    :host(.kr-button--split) {
      padding: 0;
      pointer-events: none;
    }

    .split-primary,
    .split-trigger {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      cursor: pointer;
      pointer-events: auto;
    }

    .split-primary {
      padding: 0 16px 0 22px;
      border-radius: 20px 0 0 20px;
      position: relative;
      flex: 1;
    }

    .split-trigger {
      padding: 0 14px 0 8px;
      border-radius: 0 20px 20px 0;
    }

    .split-trigger .caret {
      margin: 0;
    }

    .split-divider {
      width: 1px;
      height: 100%;
      flex-shrink: 0;
    }

    .dropdown {
      pointer-events: auto;
    }

    /* Split: zone hover — flat primary */
    :host(.kr-button--flat.kr-button--primary) .split-primary:hover,
    :host(.kr-button--flat.kr-button--primary) .split-trigger:hover {
      background: #0e1f35;
    }

    /* Split: zone hover — flat secondary */
    :host(.kr-button--flat.kr-button--secondary) .split-primary:hover,
    :host(.kr-button--flat.kr-button--secondary) .split-trigger:hover {
      background: #e5e7eb;
    }

    /* Split: zone hover — flat danger */
    :host(.kr-button--flat.kr-button--danger) .split-primary:hover,
    :host(.kr-button--flat.kr-button--danger) .split-trigger:hover {
      background: #b91c1c;
    }

    /* Split: zone hover — outline primary */
    :host(.kr-button--outline.kr-button--primary) .split-primary:hover,
    :host(.kr-button--outline.kr-button--primary) .split-trigger:hover {
      background: rgba(22, 48, 82, 0.05);
    }

    /* Split: zone hover — outline secondary */
    :host(.kr-button--outline.kr-button--secondary) .split-primary:hover,
    :host(.kr-button--outline.kr-button--secondary) .split-trigger:hover {
      background: #f9fafb;
    }

    /* Split: zone hover — outline danger */
    :host(.kr-button--outline.kr-button--danger) .split-primary:hover,
    :host(.kr-button--outline.kr-button--danger) .split-trigger:hover {
      background: rgba(219, 38, 39, 0.05);
    }

    /* Split: divider colors — flat variants */
    :host(.kr-button--flat.kr-button--primary) .split-divider {
      background: rgba(255, 255, 255, 0.3);
    }

    :host(.kr-button--flat.kr-button--secondary) .split-divider {
      background: rgba(0, 0, 0, 0.15);
    }

    :host(.kr-button--flat.kr-button--danger) .split-divider {
      background: rgba(255, 255, 255, 0.3);
    }

    /* Split: divider colors — outline variants */
    :host(.kr-button--outline.kr-button--primary) .split-divider {
      background: #163052;
    }

    :host(.kr-button--outline.kr-button--secondary) .split-divider {
      background: #d1d5db;
    }

    :host(.kr-button--outline.kr-button--danger) .split-divider {
      background: rgb(219, 38, 39);
    }

    /* Split: size small */
    :host(.kr-button--split.kr-button--small) .split-primary {
      padding: 0 12px 0 16px;
    }

    :host(.kr-button--split.kr-button--small) .split-trigger {
      padding: 0 10px;
    }

    /* Split: size large */
    :host(.kr-button--split.kr-button--large) .split-primary {
      padding: 0 18px 0 24px;
      border-radius: 30px 0 0 30px;
    }

    :host(.kr-button--split.kr-button--large) .split-trigger {
      padding: 0 16px;
      border-radius: 0 30px 30px 0;
    }

    /* Split: disabled */
    :host([disabled]) .split-primary,
    :host([disabled]) .split-trigger {
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Split: loading/success/error — overlay in primary zone only */
    :host(.kr-button--split.kr-button--loading) .split-primary .content,
    :host(.kr-button--split.kr-button--success) .split-primary .content,
    :host(.kr-button--split.kr-button--error) .split-primary .content {
      visibility: hidden;
    }

    :host(.kr-button--split.kr-button--loading) .split-trigger,
    :host(.kr-button--split.kr-button--success) .split-trigger,
    :host(.kr-button--split.kr-button--error) .split-trigger {
      pointer-events: none;
      opacity: 0.6;
    }
  `;
/**
 * Duration (ms) a success/error state is shown before auto-reset. Exposed so
 * callers can align their own follow-up timing (e.g. unmounting the button)
 * with how long the confirmation is visible.
 */
KRButton.STATE_DURATION = 2000;
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRButton.prototype, "variant", void 0);
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRButton.prototype, "color", void 0);
__decorate([
    property({
        type: String,
        reflect: true,
    })
], KRButton.prototype, "size", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRButton.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], KRButton.prototype, "href", void 0);
__decorate([
    property({ type: String })
], KRButton.prototype, "target", void 0);
__decorate([
    property({ type: Array })
], KRButton.prototype, "options", void 0);
__decorate([
    property({
        type: String,
        reflect: true,
        attribute: 'icon-position',
    })
], KRButton.prototype, "iconPosition", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true
    })
], KRButton.prototype, "split", void 0);
__decorate([
    state()
], KRButton.prototype, "_state", void 0);
__decorate([
    state()
], KRButton.prototype, "_stateText", void 0);
__decorate([
    state()
], KRButton.prototype, "_dropdownOpened", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-button')) {
    customElements.define('kr-button', KRButton);
}
//# sourceMappingURL=button.js.map